import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { 
  Receipt, 
  Plus, 
  Trash2, 
  Search, 
  FileText, 
  Send, 
  Loader2, 
  CheckCircle2, 
  XCircle,
  User,
  Package,
  Calculator,
  AlertTriangle,
  Building2,
  Truck,
  FileSearch
} from 'lucide-react';

const EmitirNFePage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [statusEmissao, setStatusEmissao] = useState(''); // Status atual da emissão
  const [resultadoEmissao, setResultadoEmissao] = useState(null); // Resultado da emissão
  const [showResultadoModal, setShowResultadoModal] = useState(false);
  const [validandoXML, setValidandoXML] = useState(false);
  const [validacaoXML, setValidacaoXML] = useState(null);
  const [showValidacaoXML, setShowValidacaoXML] = useState(false);
  const [diagnostico, setDiagnostico] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchProduct, setSearchProduct] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [fiscalConfig, setFiscalConfig] = useState(null);
  
  // Dados da NF-e
  const [nfe, setNfe] = useState({
    natureza_operacao: 'VENDA DE MERCADORIA',
    tipo_operacao: '1', // 1 = Saída
    finalidade: '1', // 1 = Normal
    destinatario: null,
    itens: [],
    informacoes_adicionais: '',
    frete: {
      modalidade: '9', // 9 = Sem frete
      valor: 0,
      transportadora: null
    }
  });

  // Carregar configurações fiscais e diagnóstico
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [fiscalResponse, diagResponse] = await Promise.all([
          api.get('/settings/fiscal'),
          api.get('/nfe/diagnostico')
        ]);
        setFiscalConfig(fiscalResponse.data);
        setDiagnostico(diagResponse.data);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };
    fetchConfig();
  }, []);

  // Helper para verificar se ambiente é produção
  const isAmbienteProducao = (ambiente) => {
    return ambiente === 1 || ambiente === '1' || ambiente === 'producao';
  };

  // Buscar clientes
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers', { params: { search: searchCustomer, limit: 20 } });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  }, [searchCustomer]);

  // Buscar produtos
  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/products', { params: { search: searchProduct, limit: 20 } });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  }, [searchProduct]);

  useEffect(() => {
    if (showCustomerModal) fetchCustomers();
  }, [showCustomerModal, fetchCustomers]);

  useEffect(() => {
    if (showProductModal) fetchProducts();
  }, [showProductModal, fetchProducts]);

  // Selecionar cliente
  const handleSelectCustomer = (customer) => {
    setNfe(prev => ({
      ...prev,
      destinatario: {
        id: customer.id,
        nome: customer.name,
        cpf_cnpj: customer.cpf_cnpj || customer.cpf || customer.cnpj || '',
        ie: customer.ie || '',
        email: customer.email || '',
        telefone: customer.phone || '',
        endereco: {
          logradouro: customer.logradouro || customer.address || '',
          numero: customer.numero || 'S/N',
          complemento: customer.complemento || '',
          bairro: customer.bairro || '',
          cidade: customer.cidade || '',
          uf: customer.uf || '',
          cep: customer.cep || '',
          codigo_municipio: customer.codigo_municipio || ''
        }
      }
    }));
    setShowCustomerModal(false);
    toast({ title: 'Cliente selecionado', description: customer.name });
  };

  // Adicionar produto
  const handleAddProduct = (product) => {
    const novoItem = {
      id: product.id,
      codigo: product.code || product.sku || '',
      descricao: product.name,
      ncm: product.ncm || '00000000',
      cfop: product.cfop || '5102',
      unidade: product.unit || 'UN',
      quantidade: 1,
      valor_unitario: parseFloat(product.price) || 0,
      valor_total: parseFloat(product.price) || 0,
      icms_cst: product.icms_cst || '00',
      icms_aliquota: parseFloat(product.icms_aliquota) || 0,
      pis_cst: product.pis_cst || '01',
      cofins_cst: product.cofins_cst || '01'
    };
    
    setNfe(prev => ({
      ...prev,
      itens: [...prev.itens, novoItem]
    }));
    setShowProductModal(false);
    toast({ title: 'Produto adicionado', description: product.name });
  };

  // Atualizar quantidade do item
  const handleUpdateItemQuantidade = (index, quantidade) => {
    setNfe(prev => {
      const itens = [...prev.itens];
      itens[index].quantidade = parseFloat(quantidade) || 0;
      itens[index].valor_total = itens[index].quantidade * itens[index].valor_unitario;
      return { ...prev, itens };
    });
  };

  // Atualizar valor unitário do item
  const handleUpdateItemValor = (index, valor) => {
    setNfe(prev => {
      const itens = [...prev.itens];
      itens[index].valor_unitario = parseFloat(valor) || 0;
      itens[index].valor_total = itens[index].quantidade * itens[index].valor_unitario;
      return { ...prev, itens };
    });
  };

  // Remover item
  const handleRemoveItem = (index) => {
    setNfe(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };

  // Calcular totais
  const calcularTotais = () => {
    const totalProdutos = nfe.itens.reduce((sum, item) => sum + item.valor_total, 0);
    const totalFrete = parseFloat(nfe.frete.valor) || 0;
    const totalNota = totalProdutos + totalFrete;
    return { totalProdutos, totalFrete, totalNota };
  };

  // Estado para validação
  const [validacao, setValidacao] = useState(null);
  const [showValidacao, setShowValidacao] = useState(false);

  // Emitir NF-e
  const handleEmitirNFe = async () => {
    // Validações básicas locais
    if (!nfe.destinatario) {
      toast({ title: 'Erro', description: 'Selecione um destinatário', variant: 'destructive' });
      return;
    }
    if (nfe.itens.length === 0) {
      toast({ title: 'Erro', description: 'Adicione pelo menos um item', variant: 'destructive' });
      return;
    }
    if (!nfe.destinatario.cpf_cnpj) {
      toast({ title: 'Erro', description: 'O destinatário precisa ter CPF/CNPJ cadastrado', variant: 'destructive' });
      return;
    }

    try {
      setEmitindo(true);
      
      const totais = calcularTotais();
      
      const payload = {
        natureza_operacao: nfe.natureza_operacao,
        tipo_operacao: nfe.tipo_operacao,
        finalidade: nfe.finalidade,
        destinatario: nfe.destinatario,
        itens: nfe.itens.map(item => ({
          codigo: item.codigo,
          descricao: item.descricao,
          ncm: item.ncm,
          cfop: item.cfop,
          unidade: item.unidade,
          quantidade: item.quantidade,
          valor_unitario: item.valor_unitario,
          valor_total: item.valor_total,
          icms_cst: item.icms_cst,
          icms_aliquota: item.icms_aliquota,
          pis_cst: item.pis_cst,
          cofins_cst: item.cofins_cst
        })),
        frete: nfe.frete,
        informacoes_adicionais: nfe.informacoes_adicionais,
        totais: {
          valor_produtos: totais.totalProdutos,
          valor_frete: totais.totalFrete,
          valor_total: totais.totalNota
        }
      };

      // PRIMEIRO: Validar antes de emitir
      try {
        const validacaoResponse = await api.post('/nfe/validar', payload);
        if (!validacaoResponse.data.valido) {
          setValidacao(validacaoResponse.data);
          setShowValidacao(true);
          toast({ 
            title: 'Problemas encontrados', 
            description: `${validacaoResponse.data.problemas.length} erro(s) de validação. Verifique os detalhes.`,
            variant: 'destructive' 
          });
          return;
        }
        // Se há avisos, mostrar mas continuar
        if (validacaoResponse.data.avisos?.length > 0) {
          setValidacao(validacaoResponse.data);
        }
      } catch (validError) {
        console.warn('Erro na validação prévia, continuando...', validError);
      }

      // EMITIR
      setStatusEmissao('Enviando para SEFAZ...');
      const response = await api.post('/nfe/emitir', payload);
      
      // Guardar resultado completo
      setResultadoEmissao(response.data);
      setShowResultadoModal(true);
      
      if (response.data.success) {
        toast({ 
          title: response.data.simulado ? 'NF-e Emitida (Simulação)' : 'NF-e Emitida com Sucesso!', 
          description: `Número: ${response.data.numero} | ${response.data.simulado ? 'SIMULAÇÃO - Configure certificado para enviar à SEFAZ' : 'Chave: ' + response.data.chave?.substring(0, 25) + '...'}`
        });
        
        // Limpar formulário
        setNfe({
          natureza_operacao: 'VENDA DE MERCADORIA',
          tipo_operacao: '1',
          finalidade: '1',
          destinatario: null,
          itens: [],
          informacoes_adicionais: '',
          frete: { modalidade: '9', valor: 0, transportadora: null }
        });
        setValidacao(null);
      } else {
        toast({ 
          title: 'Erro na Emissão', 
          description: response.data.error || response.data.message || 'Erro desconhecido', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Erro ao emitir NF-e:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erro ao emitir NF-e';
      setResultadoEmissao({
        success: false,
        error: errorMessage,
        ambiente: fiscalConfig?.ambiente === 'producao' ? 'Produção' : 'Homologação'
      });
      setShowResultadoModal(true);
      toast({ 
        title: 'Erro na Emissão', 
        description: errorMessage,
        variant: 'destructive' 
      });
    } finally {
      setEmitindo(false);
      setStatusEmissao('');
    }
  };

  // Validar XML antes de emitir
  const handleValidarXML = async () => {
    setValidandoXML(true);
    try {
      const payload = {
        natureza_operacao: nfe.natureza_operacao,
        tipo_operacao: nfe.tipo_operacao,
        finalidade: nfe.finalidade,
        destinatario: nfe.destinatario,
        itens: nfe.itens.map(item => ({
          ...item,
          valor_total: parseFloat(item.quantidade || 0) * parseFloat(item.valor_unitario || 0)
        })),
        frete: nfe.frete,
        informacoes_adicionais: nfe.informacoes_adicionais,
        totais: calcularTotais()
      };

      const response = await api.post('/nfe/validar-xml', payload);
      setValidacaoXML(response.data);
      setShowValidacaoXML(true);
      
      if (response.data.valido) {
        toast({
          title: 'XML Válido',
          description: 'Estrutura do XML está correta. Pronto para emitir.'
        });
      } else {
        toast({
          title: 'Problemas no XML',
          description: `${response.data.total_erros} erro(s) encontrado(s)`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erro ao validar XML:', error);
      toast({
        title: 'Erro na Validação',
        description: error.response?.data?.detail || 'Erro ao validar XML',
        variant: 'destructive'
      });
    } finally {
      setValidandoXML(false);
    }
  };

  const totais = calcularTotais();

  return (
    <div className="p-6 space-y-6" data-testid="emitir-nfe-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Receipt className="w-8 h-8 text-blue-600" />
            Emitir NF-e
          </h1>
          <p className="text-slate-500 mt-1">Emissão de Nota Fiscal Eletrônica (Modelo 55)</p>
        </div>
        
        {fiscalConfig && (
          <Badge variant={isAmbienteProducao(fiscalConfig.ambiente_nfe) ? 'default' : 'secondary'}>
            {isAmbienteProducao(fiscalConfig.ambiente_nfe) ? 'Produção' : 'Homologação'}
          </Badge>
        )}
      </div>

      {/* Alerta de ambiente */}
      {fiscalConfig && !isAmbienteProducao(fiscalConfig.ambiente_nfe) && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">Ambiente de Homologação</p>
            <p className="text-sm text-amber-700">As notas emitidas são apenas para teste e não têm validade fiscal.</p>
          </div>
        </div>
      )}

      {/* Card de Status da Configuração */}
      {diagnostico && (
        <div className={`p-4 rounded-lg border ${diagnostico.pronto_emissao_real ? 'bg-green-50 border-green-300' : (diagnostico.total_problemas > 0 ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300')}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {diagnostico.pronto_emissao_real ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-700">Configuração OK - Emissão Real</span>
                </>
              ) : diagnostico.total_problemas > 0 ? (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-700">{diagnostico.total_problemas} Problema(s) - Verifique Configurações</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <span className="font-medium text-yellow-700">Modo Simulação - Configure certificado para emissão real</span>
                </>
              )}
            </div>
            <span className={`text-xs px-2 py-1 rounded ${diagnostico.configuracoes?.ambiente?.valor === 'producao' ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'}`}>
              {diagnostico.configuracoes?.ambiente?.valor === 'producao' ? '🔴 PRODUÇÃO' : '🟡 HOMOLOGAÇÃO'}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="flex items-center gap-1">
              {diagnostico.configuracoes?.certificado?.tem_arquivo ? (
                <CheckCircle2 className="w-3 h-3 text-green-600" />
              ) : (
                <XCircle className="w-3 h-3 text-red-500" />
              )}
              <span>Certificado</span>
            </div>
            <div className="flex items-center gap-1">
              {diagnostico.configuracoes?.empresa?.cnpj !== 'NÃO CONFIGURADO' ? (
                <CheckCircle2 className="w-3 h-3 text-green-600" />
              ) : (
                <XCircle className="w-3 h-3 text-red-500" />
              )}
              <span>CNPJ</span>
            </div>
            <div className="flex items-center gap-1">
              {diagnostico.configuracoes?.endereco?.logradouro !== 'NÃO CONFIGURADO' ? (
                <CheckCircle2 className="w-3 h-3 text-green-600" />
              ) : (
                <XCircle className="w-3 h-3 text-red-500" />
              )}
              <span>Endereço</span>
            </div>
            <div className="flex items-center gap-1">
              {diagnostico.configuracoes?.endereco?.codigo_ibge !== 'NÃO CONFIGURADO' ? (
                <CheckCircle2 className="w-3 h-3 text-green-600" />
              ) : (
                <XCircle className="w-3 h-3 text-red-500" />
              )}
              <span>Cód. IBGE</span>
            </div>
          </div>
          
          {/* CRT e Empresa */}
          <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600 flex gap-4">
            <span>CRT: <strong>{diagnostico.configuracoes?.empresa?.crt_descricao}</strong></span>
            <span>Empresa: <strong>{diagnostico.configuracoes?.empresa?.razao_social?.substring(0, 30)}</strong></span>
          </div>
          
          {/* Problemas */}
          {diagnostico.problemas?.length > 0 && (
            <div className="mt-2 text-xs text-red-600">
              {diagnostico.problemas.slice(0, 2).map((p, i) => (
                <p key={i}>• {p.mensagem}</p>
              ))}
              {diagnostico.problemas.length > 2 && (
                <p className="text-red-500">+ {diagnostico.problemas.length - 2} mais problemas</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dados da Operação */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Dados da Operação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Natureza da Operação</Label>
                  <Input 
                    value={nfe.natureza_operacao}
                    onChange={(e) => setNfe(prev => ({ ...prev, natureza_operacao: e.target.value }))}
                    placeholder="Ex: VENDA DE MERCADORIA"
                  />
                </div>
                <div>
                  <Label>Finalidade</Label>
                  <Select 
                    value={nfe.finalidade} 
                    onValueChange={(v) => setNfe(prev => ({ ...prev, finalidade: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Normal</SelectItem>
                      <SelectItem value="2">2 - Complementar</SelectItem>
                      <SelectItem value="3">3 - Ajuste</SelectItem>
                      <SelectItem value="4">4 - Devolução</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Destinatário */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Destinatário
              </CardTitle>
              <CardDescription>Selecione o cliente para quem será emitida a nota</CardDescription>
            </CardHeader>
            <CardContent>
              {nfe.destinatario ? (
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-lg">{nfe.destinatario.nome}</p>
                      <p className="text-sm text-slate-600">CPF/CNPJ: {nfe.destinatario.cpf_cnpj || 'Não informado'}</p>
                      {nfe.destinatario.email && (
                        <p className="text-sm text-slate-500">{nfe.destinatario.email}</p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowCustomerModal(true)}>
                      Alterar
                    </Button>
                  </div>
                  {nfe.destinatario.endereco?.logradouro && (
                    <div className="pt-2 border-t text-sm text-slate-600">
                      <p>{nfe.destinatario.endereco.logradouro}, {nfe.destinatario.endereco.numero}</p>
                      <p>{nfe.destinatario.endereco.bairro} - {nfe.destinatario.endereco.cidade}/{nfe.destinatario.endereco.uf}</p>
                      <p>CEP: {nfe.destinatario.endereco.cep}</p>
                    </div>
                  )}
                </div>
              ) : (
                <Button onClick={() => setShowCustomerModal(true)} className="w-full">
                  <Search className="w-4 h-4 mr-2" />
                  Selecionar Cliente
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Itens */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Itens da Nota
              </CardTitle>
              <CardDescription>Adicione os produtos que serão incluídos na NF-e</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={() => setShowProductModal(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Produto
              </Button>

              {nfe.itens.length > 0 ? (
                <div className="space-y-3">
                  {nfe.itens.map((item, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{item.descricao}</p>
                          <p className="text-sm text-slate-500">
                            Código: {item.codigo} | NCM: {item.ncm} | CFOP: {item.cfop}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div>
                          <Label className="text-xs">Quantidade</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={item.quantidade}
                            onChange={(e) => handleUpdateItemQuantidade(index, e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Valor Unitário</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={item.valor_unitario}
                            onChange={(e) => handleUpdateItemValor(index, e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Valor Total</Label>
                          <Input 
                            value={item.valor_total.toFixed(2)}
                            disabled
                            className="bg-slate-100"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum item adicionado</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Frete */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Frete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Modalidade do Frete</Label>
                  <Select 
                    value={nfe.frete.modalidade} 
                    onValueChange={(v) => setNfe(prev => ({ 
                      ...prev, 
                      frete: { ...prev.frete, modalidade: v }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 - Por conta do Emitente</SelectItem>
                      <SelectItem value="1">1 - Por conta do Destinatário</SelectItem>
                      <SelectItem value="2">2 - Por conta de Terceiros</SelectItem>
                      <SelectItem value="9">9 - Sem Frete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor do Frete (R$)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={nfe.frete.valor}
                    onChange={(e) => setNfe(prev => ({ 
                      ...prev, 
                      frete: { ...prev.frete, valor: parseFloat(e.target.value) || 0 }
                    }))}
                    disabled={nfe.frete.modalidade === '9'}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informações Adicionais */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Adicionais</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea 
                value={nfe.informacoes_adicionais}
                onChange={(e) => setNfe(prev => ({ ...prev, informacoes_adicionais: e.target.value }))}
                placeholder="Informações complementares que serão impressas na nota..."
                rows={3}
              />
            </CardContent>
          </Card>
        </div>

        {/* Coluna Lateral - Resumo */}
        <div className="space-y-6">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Resumo da Nota
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Qtd. Itens:</span>
                  <span className="font-medium">{nfe.itens.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Produtos:</span>
                  <span className="font-medium">R$ {totais.totalProdutos.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Frete:</span>
                  <span className="font-medium">R$ {totais.totalFrete.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold">Total da Nota:</span>
                  <span className="font-bold text-lg text-green-600">R$ {totais.totalNota.toFixed(2)}</span>
                </div>
              </div>

              <div className="pt-4 space-y-3">
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={handleEmitirNFe}
                  disabled={emitindo || !nfe.destinatario || nfe.itens.length === 0}
                >
                  {emitindo ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {statusEmissao || 'Processando...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Emitir NF-e
                    </>
                  )}
                </Button>

                {/* Indicador de ambiente */}
                {fiscalConfig && (
                  <div className={`text-center text-xs py-1 px-2 rounded ${(fiscalConfig.ambiente_nfe_normalizado || fiscalConfig.ambiente) === 'producao' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    Ambiente NF-e: {(fiscalConfig.ambiente_nfe_normalizado || fiscalConfig.ambiente) === 'producao' ? '🔴 Produção' : '🟡 Homologação'}
                  </div>
                )}

                {/* Botão de Validar XML */}
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={handleValidarXML}
                  disabled={validandoXML || nfe.itens.length === 0}
                >
                  {validandoXML ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Validando...
                    </>
                  ) : (
                    <>
                      <FileSearch className="w-4 h-4 mr-2" />
                      Validar XML
                    </>
                  )}
                </Button>

                {/* Checklist */}
                <div className="text-xs space-y-1">
                  <div className="flex items-center gap-2">
                    {nfe.destinatario ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-slate-300" />
                    )}
                    <span className={nfe.destinatario ? 'text-green-700' : 'text-slate-400'}>
                      Destinatário selecionado
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {nfe.itens.length > 0 ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-slate-300" />
                    )}
                    <span className={nfe.itens.length > 0 ? 'text-green-700' : 'text-slate-400'}>
                      Itens adicionados
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal Seleção de Cliente */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Selecionar Cliente</DialogTitle>
            <DialogDescription>Busque e selecione o destinatário da nota</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input 
              placeholder="Buscar por nome, CPF/CNPJ..."
              value={searchCustomer}
              onChange={(e) => setSearchCustomer(e.target.value)}
            />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {customers.map((customer) => (
                <div 
                  key={customer.id}
                  className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => handleSelectCustomer(customer)}
                >
                  <p className="font-medium">{customer.name}</p>
                  <p className="text-sm text-slate-500">
                    {customer.cpf_cnpj || customer.cpf || customer.cnpj || 'CPF/CNPJ não informado'}
                    {customer.email && ` • ${customer.email}`}
                  </p>
                </div>
              ))}
              {customers.length === 0 && (
                <p className="text-center text-slate-500 py-4">Nenhum cliente encontrado</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Seleção de Produto */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar Produto</DialogTitle>
            <DialogDescription>Busque e adicione produtos à nota</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input 
              placeholder="Buscar por nome, código..."
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
            />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {products.map((product) => (
                <div 
                  key={product.id}
                  className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer flex justify-between items-center"
                  onClick={() => handleAddProduct(product)}
                >
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-slate-500">
                      Código: {product.code || product.sku || 'N/A'} • NCM: {product.ncm || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">R$ {parseFloat(product.price || 0).toFixed(2)}</p>
                    <p className="text-xs text-slate-500">{product.unit || 'UN'}</p>
                  </div>
                </div>
              ))}
              {products.length === 0 && (
                <p className="text-center text-slate-500 py-4">Nenhum produto encontrado</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Validação - Erros e Avisos */}
      <Dialog open={showValidacao} onOpenChange={setShowValidacao}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {validacao?.valido ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              {validacao?.valido ? 'Validação OK' : 'Problemas Encontrados'}
            </DialogTitle>
            <DialogDescription>
              {validacao?.valido 
                ? `Modo de emissão: ${validacao?.modo_emissao}` 
                : `${validacao?.problemas?.length || 0} erro(s) que impedem a emissão`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Problemas (erros) */}
            {validacao?.problemas?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-red-600 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Erros que impedem a emissão:
                </h4>
                <div className="space-y-2">
                  {validacao.problemas.map((p, idx) => (
                    <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="font-medium text-red-800">{p.erro}</p>
                      <p className="text-sm text-red-600">
                        <span className="font-mono bg-red-100 px-1 rounded">{p.campo}</span>
                        {p.solucao && <span className="ml-2">→ {p.solucao}</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Avisos */}
            {validacao?.avisos?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-amber-600 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Avisos (não impedem a emissão):
                </h4>
                <div className="space-y-2">
                  {validacao.avisos.map((a, idx) => (
                    <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="font-medium text-amber-800">{a.aviso}</p>
                      <p className="text-sm text-amber-600">
                        <span className="font-mono bg-amber-100 px-1 rounded">{a.campo}</span>
                        {a.nota && <span className="ml-2">→ {a.nota}</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Resumo */}
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="text-sm">
                <strong>Ambiente:</strong> {validacao?.ambiente === 'producao' ? 'Produção' : 'Homologação'}
              </p>
              <p className="text-sm">
                <strong>Modo de emissão:</strong> {validacao?.modo_emissao}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidacao(false)}>
              Fechar e Corrigir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Resultado da Emissão */}
      <Dialog open={showResultadoModal} onOpenChange={setShowResultadoModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resultadoEmissao?.success ? (
                <>
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  NF-e Emitida com Sucesso
                </>
              ) : (
                <>
                  <XCircle className="w-6 h-6 text-red-500" />
                  Erro na Emissão
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Status Geral */}
            <div className={`p-4 rounded-lg ${resultadoEmissao?.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {resultadoEmissao?.success ? (
                <div className="space-y-2">
                  <p className="font-medium text-green-800">
                    {resultadoEmissao.simulado ? '⚠️ MODO SIMULAÇÃO' : '✅ Autorizada pela SEFAZ'}
                  </p>
                  {resultadoEmissao.simulado && (
                    <p className="text-sm text-orange-600">
                      Nota emitida em modo simulação. Configure o certificado digital para enviar à SEFAZ.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="font-medium text-red-800">Não foi possível emitir a NF-e</p>
                  <p className="text-sm text-red-600">{resultadoEmissao?.error || 'Erro desconhecido'}</p>
                </div>
              )}
            </div>

            {/* Detalhes */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-slate-50 rounded">
                  <p className="text-slate-500">Ambiente</p>
                  <p className="font-semibold flex items-center gap-1">
                    {resultadoEmissao?.ambiente === 'Produção' ? (
                      <><span className="w-2 h-2 bg-red-500 rounded-full"></span> Produção</>
                    ) : (
                      <><span className="w-2 h-2 bg-yellow-500 rounded-full"></span> Homologação</>
                    )}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded">
                  <p className="text-slate-500">Status</p>
                  <p className="font-semibold">{resultadoEmissao?.status || '-'}</p>
                </div>
              </div>

              {resultadoEmissao?.success && (
                <>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 bg-slate-50 rounded">
                      <p className="text-slate-500">Número</p>
                      <p className="font-semibold">{resultadoEmissao?.numero || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded">
                      <p className="text-slate-500">Série</p>
                      <p className="font-semibold">{resultadoEmissao?.serie || '-'}</p>
                    </div>
                  </div>

                  {resultadoEmissao?.chave && (
                    <div className="p-3 bg-slate-50 rounded text-sm">
                      <p className="text-slate-500">Chave de Acesso</p>
                      <p className="font-mono text-xs break-all">{resultadoEmissao.chave}</p>
                    </div>
                  )}

                  {resultadoEmissao?.protocolo && !resultadoEmissao?.simulado && (
                    <div className="p-3 bg-slate-50 rounded text-sm">
                      <p className="text-slate-500">Protocolo SEFAZ</p>
                      <p className="font-semibold">{resultadoEmissao.protocolo}</p>
                    </div>
                  )}
                </>
              )}

              {resultadoEmissao?.message && (
                <div className="p-3 bg-blue-50 rounded text-sm">
                  <p className="text-blue-700">{resultadoEmissao.message}</p>
                </div>
              )}

              {resultadoEmissao?.aviso && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                  <p className="text-yellow-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {resultadoEmissao.aviso}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            {resultadoEmissao?.success && (
              <Button variant="outline" onClick={() => window.location.href = '/gerenciar-nfe'}>
                Ver NF-e Emitidas
              </Button>
            )}
            <Button onClick={() => setShowResultadoModal(false)}>
              {resultadoEmissao?.success ? 'Nova NF-e' : 'Fechar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Validação de XML */}
      <Dialog open={showValidacaoXML} onOpenChange={setShowValidacaoXML}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {validacaoXML?.valido ? (
                <>
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  XML Válido
                </>
              ) : (
                <>
                  <XCircle className="w-6 h-6 text-red-500" />
                  Problemas no XML
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Contexto */}
            {validacaoXML?.contexto && (
              <div className="p-3 bg-slate-50 rounded-lg space-y-1 text-sm">
                <p><strong>CRT:</strong> {validacaoXML.contexto.crt} - {validacaoXML.contexto.crt_descricao}</p>
                <p><strong>Ambiente:</strong> {validacaoXML.contexto.ambiente}</p>
                <p><strong>UF:</strong> {validacaoXML.contexto.uf}</p>
                <p><strong>CNPJ:</strong> {validacaoXML.contexto.cnpj}</p>
                <p><strong>Empresa:</strong> {validacaoXML.contexto.razao_social}</p>
              </div>
            )}

            {/* Erros */}
            {validacaoXML?.erros?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-red-700 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Erros ({validacaoXML.erros.length})
                </h4>
                <div className="space-y-1">
                  {validacaoXML.erros.map((erro, idx) => (
                    <div key={idx} className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      {erro}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Avisos */}
            {validacaoXML?.avisos?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-amber-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Avisos ({validacaoXML.avisos.length})
                </h4>
                <div className="space-y-1">
                  {validacaoXML.avisos.map((aviso, idx) => (
                    <div key={idx} className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                      {aviso}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status do XML */}
            {validacaoXML?.xml_gerado !== undefined && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p><strong>XML Gerado:</strong> {validacaoXML.xml_gerado ? '✅ Sim' : '❌ Não'}</p>
                {validacaoXML.xml_tamanho && <p><strong>Tamanho:</strong> {validacaoXML.xml_tamanho} bytes</p>}
                {validacaoXML.xml_estrutura_ok !== undefined && (
                  <p><strong>Estrutura:</strong> {validacaoXML.xml_estrutura_ok ? '✅ OK' : '❌ Inválida'}</p>
                )}
                {validacaoXML.xml_erro && (
                  <p className="text-red-600"><strong>Erro XML:</strong> {validacaoXML.xml_erro}</p>
                )}
              </div>
            )}

            {/* Mensagem de sucesso */}
            {validacaoXML?.valido && validacaoXML?.erros?.length === 0 && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">XML está pronto para emissão!</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowValidacaoXML(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmitirNFePage;
